import { t, Trans } from "@web-monorepo/i18n/macro";
import { DDSButton } from "@web-monorepo/dds";
import { IconPencil } from "@web-monorepo/dds-icons";
import { useSideEffectOnMount } from "@web-monorepo/hooks";
import ToolkitAppScroller from "components/BeyondToolkit/ToolkitAppScroller";
import { Person } from "components/PersonListItem";
import ToolkitCard from "components/ToolkitCard";
import { useCallback, useMemo, useState } from "react";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { WHOSE_TURN } from "utils/beyondToolkitTools";
import { sendUsage } from "utils/freemium";
import EditPeopleModal from "./EditPeopleModal";
import styles from "./WhoseTurn.module.css";
import WhoseTurnCardContent from "./WhoseTurnCardContent";

const rolling = "/static/media/rolling-loop.mp3";
const rollingCaptions = "/static/media/rolling-loop.vtt";
const finish = "/static/media/finish.mp3";
const finishCaptions = "/static/media/finish.vtt";

type WhoseTurnProps = {
  students: Person[];
};

export default function WhoseTurn(props: WhoseTurnProps) {
  const [additionalPersons, setAdditionalPersons] = useState<Person[]>([]);
  const [deselectedIds, setDeselectedIds] = useState<string[]>([]);
  const [showPeopleModal, setShowPeopleModal] = useState(false);

  const allPersons = useMemo(() => {
    return props.students.concat(additionalPersons);
  }, [props.students, additionalPersons]);

  const filteredPersons = useMemo(() => {
    return allPersons.filter((p) => !deselectedIds.includes(p._id));
  }, [allPersons, deselectedIds]);

  const { title, imgSrc } = WHOSE_TURN();

  const cancelEditPeople = useCallback(() => {
    setShowPeopleModal(false);
  }, [setShowPeopleModal]);

  const completeEditPeople = useCallback(
    (newAdditionalPersons: Person[], newDeselectedIds: string[]) => {
      setShowPeopleModal(false);
      setAdditionalPersons([...additionalPersons, ...newAdditionalPersons]);
      setDeselectedIds(newDeselectedIds);
    },
    [setShowPeopleModal, setAdditionalPersons, additionalPersons, setDeselectedIds],
  );

  useSideEffectOnMount(() => {
    sendUsage("whoseTurn");
  });

  if (showPeopleModal) {
    return (
      <EditPeopleModal
        onClose={cancelEditPeople}
        onComplete={completeEditPeople}
        persons={allPersons}
        initialDeselectedIds={deselectedIds}
      />
    );
  }

  return (
    <ToolkitAppScroller title={title} variant="whoseTurn" imgSrc={imgSrc}>
      <audio id="rolling-audio" src={rolling}>
        <track kind="captions" src={rollingCaptions} srcLang="en" label={t`English`} default />
      </audio>
      <audio id="finish-audio" src={finish}>
        <track kind="captions" src={finishCaptions} srcLang="en" label={t`English`} default />
      </audio>
      <div className={styles.cardWrap}>
        <ToolkitCard>
          <WhoseTurnCardContent persons={filteredPersons} />
        </ToolkitCard>
      </div>
      <div className={styles.editWrap}>
        <DDSButton
          data-name="whose_turn:edit_people"
          variant="tertiary"
          onClick={() => {
            logParentToolkitEvent({ eventName: `whose_turn.edit.tap` });
            setShowPeopleModal(true);
          }}
        >
          <IconPencil className={styles.pencilIcon} />
          <Trans>Edit people</Trans>
        </DDSButton>
      </div>
    </ToolkitAppScroller>
  );
}
